export const findCourseTreeNode = (courseTree, findFn) => {
  if (!courseTree) return null

  // Check if current node matches
  if (findFn?.(courseTree)) {
    return courseTree
  }

  // Recursively search children
  for (const node of courseTree?.children || []) {
    if (findFn?.(node)) {
      return node
    }

    // Only traverse non-leaf nodes
    if (!node.leaf) {
      const result = findCourseTreeNode(node, findFn)
      if (result) {
        return result
      }
    }
  }

  return null
}

export const findCourseTreeNodeById = (
  courseTree,
  courseNodeId,
  options = {},
) => {
  const { matchLeafNodeId = false, useStartsWith = false } = options

  return findCourseTreeNode(courseTree, node => {
    // Always check contentfulId
    if (node.contentfulId === courseNodeId) {
      return true
    }

    // Optionally check leafNodeId
    if (matchLeafNodeId && node.leafNodeId) {
      if (useStartsWith) {
        return (
          node.leafNodeId === courseNodeId ||
          node.leafNodeId.startsWith(courseNodeId)
        )
      }
      return node.leafNodeId === courseNodeId
    }

    return false
  })
}

export const findAllCourseTreeNodes = (courseTree, findFn) => {
  if (!courseTree) return []

  const matches = []

  // Check current node
  if (findFn?.(courseTree)) {
    matches.push(courseTree)
  }

  // Recursively search children
  for (const node of courseTree?.children || []) {
    if (findFn?.(node)) {
      matches.push(node)
    }

    if (!node.leaf) {
      matches.push(...findAllCourseTreeNodes(node, findFn))
    }
  }

  return matches
}

export const buildLeafNodeIdMap = (treeNode, map = {}) => {
  if (!treeNode) return map

  if (treeNode.contentfulId && treeNode.leafNodeId) {
    map[treeNode.contentfulId] = treeNode.leafNodeId
  }

  for (const child of treeNode.children || []) {
    buildLeafNodeIdMap(child, map)
  }

  return map
}

export const parseLessonIndexFromSlug = lessonSlug => {
  if (!lessonSlug || typeof lessonSlug !== 'string') return null

  const match = lessonSlug.trim().match(/lesson-(\d+)/i)
  if (!match?.[1]) return null

  const parsed = parseInt(match[1], 10)
  if (Number.isNaN(parsed) || parsed < 1) return null

  return parsed - 1
}

export const getOrderedLeafNodeIdsFromCourseTree = courseTreeData => {
  if (!courseTreeData) return []

  const subTreeNodes =
    courseTreeData.subTreeNodes || courseTreeData.sub_tree_nodes || {}
  const siteEducatorScopes =
    subTreeNodes.siteEducator || subTreeNodes.site_educator || []

  if (!Array.isArray(siteEducatorScopes)) return []

  const lessonScope = siteEducatorScopes.find(
    scope =>
      scope.lessonInteractionType === 'lesson' ||
      scope.lesson_interaction_type === 'lesson',
  )

  return lessonScope?.leafExternalIds || lessonScope?.leaf_external_ids || []
}

export const resolveIndexBasedLeafNodeId = ({ courseTreeData, lessonSlug }) => {
  const lessonIndex = parseLessonIndexFromSlug(lessonSlug)
  if (lessonIndex == null) return null

  const orderedLeafNodeIds = getOrderedLeafNodeIdsFromCourseTree(courseTreeData)
  return orderedLeafNodeIds[lessonIndex] || null
}
